/**
 * Phase 5 — Admin Architect Request Queue
 * Route: /admin/architect-requests
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ClipboardList, RefreshCw, X, UserPlus, Eye, Ban, Archive, Clock, XCircle } from 'lucide-react';
import theme from '../theme';
import AssistantDraftPanel from '../components/AssistantDraftPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LIST = [
  'all', 'submitted', 'admin_review', 'assigned', 'accepted', 'declined',
  'in_progress', 'completed', 'canceled', 'rejected', 'archived', 'draft',
];
const SPECIALTIES = [
  '', 'licensed_investment_professional', 'registered_broker', 'investment_adviser',
  'portfolio_manager', 'compliance_reviewer', 'business_strategist',
  'brand_advisor', 'offer_consultant', 'market_specialist',
  'general_reviewer', 'other',
];
const RISK = ['', 'low', 'moderate', 'high', 'unknown', 'not_applicable'];
const STATUS_COLOR = {
  draft: theme.colors.text.muted, submitted: theme.colors.status.info,
  admin_review: theme.colors.status.warning, assigned: theme.colors.status.info,
  accepted: theme.colors.status.success, declined: theme.colors.status.error,
  in_progress: theme.colors.status.warning, completed: theme.colors.status.success,
  canceled: theme.colors.text.muted, rejected: theme.colors.status.error, archived: theme.colors.text.muted,
};

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 20, boxShadow: theme.colors.shadows.sm,
};
const inputStyle = {
  padding: '8px 10px', border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.sm, fontSize: 13, outline: 'none',
};

function Pill({ status }) {
  const c = STATUS_COLOR[status] || theme.colors.text.muted;
  return (
    <span data-testid={`admin-req-status-${status}`} style={{ padding: '3px 10px', borderRadius: 999,
      background: `${c}1A`, color: c, fontSize: 11, fontWeight: 800, textTransform: 'uppercase' }}>
      {(status || '').replace(/_/g, ' ')}
    </span>
  );
}

export default function AdminArchitectRequestsPage() {
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [architects, setArchitects] = useState([]);
  const [filters, setFilters] = useState({ status: '', requested_specialty: '', risk_category: '', search: '' });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [assignId, setAssignId] = useState('');
  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filters.status) p.set('status', filters.status);
      if (filters.requested_specialty) p.set('requested_specialty', filters.requested_specialty);
      if (filters.risk_category) p.set('risk_category', filters.risk_category);
      if (filters.search) p.set('search', filters.search);
      const res = await axios.get(`${API}/admin/architect-requests?${p.toString()}`, auth);
      setRows(res.data?.requests || []);
      setCounts(res.data?.status_counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load queue');
    } finally { setLoading(false); }
  }, [auth, filters]);

  const loadArchitects = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/architects?approval_status=approved`, auth);
      setArchitects(res.data?.architects || []);
    } catch (_) { /* ignore */ }
  }, [auth]);

  useEffect(() => { load(); loadArchitects(); }, [load, loadArchitects]);

  const assign = async () => {
    if (!selected || !assignId) return;
    setActing(true);
    try {
      await axios.patch(`${API}/admin/architect-requests/${selected.id}/assign`,
        { architect_id: assignId }, auth);
      toast.success('Assigned to Architect');
      setSelected(null); setAssignId(''); setNotes(''); setRejectionReason('');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Assign failed'); }
    finally { setActing(false); }
  };

  const reviewAction = async (action) => {
    if (!selected) return;
    setActing(true);
    try {
      await axios.patch(`${API}/admin/architect-requests/${selected.id}/review`,
        { action, admin_notes: notes || null, rejection_reason: rejectionReason || null }, auth);
      toast.success(action);
      setSelected(null); setAssignId(''); setNotes(''); setRejectionReason('');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    finally { setActing(false); }
  };

  return (
    <div data-testid="admin-architect-requests-page" style={{ padding: '24px 28px', maxWidth: 1300, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ClipboardList size={22} color={theme.colors.primary} />
            <div style={{ fontSize: 22, fontWeight: 900 }}>Architect Request Queue</div>
          </div>
          <div style={{ color: theme.colors.text.muted, fontSize: 13, marginTop: 4, maxWidth: 720 }}>
            Review Architect request packets and assign approved Architects to eligible requests.
          </div>
        </div>
        <button data-testid="admin-arch-req-refresh-btn" onClick={load}
          style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`,
            background: theme.colors.bg.card, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div style={{ ...cardStyle, marginTop: 14 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end', marginBottom: 10 }}>
          <div><label style={{ fontSize: 11, fontWeight: 700 }}>Status</label>
            <div><select data-testid="admin-req-status-filter" style={inputStyle}
              value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}>
              <option value="">any</option>
              {STATUS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
            </select></div>
          </div>
          <div><label style={{ fontSize: 11, fontWeight: 700 }}>Specialty</label>
            <div><select data-testid="admin-req-specialty-filter" style={inputStyle}
              value={filters.requested_specialty}
              onChange={e => setFilters({ ...filters, requested_specialty: e.target.value })}>
              <option value="">any</option>
              {SPECIALTIES.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}
            </select></div>
          </div>
          <div><label style={{ fontSize: 11, fontWeight: 700 }}>Risk</label>
            <div><select data-testid="admin-req-risk-filter" style={inputStyle}
              value={filters.risk_category}
              onChange={e => setFilters({ ...filters, risk_category: e.target.value })}>
              <option value="">any</option>
              {RISK.filter(Boolean).map(r => <option key={r} value={r}>{r}</option>)}
            </select></div>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}><label style={{ fontSize: 11, fontWeight: 700 }}>Search</label>
            <div><input data-testid="admin-req-search" style={{ ...inputStyle, width: '100%' }}
              value={filters.search} placeholder="business, kickback, email…"
              onChange={e => setFilters({ ...filters, search: e.target.value })} /></div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {Object.entries(counts).map(([k, v]) => (
            <span key={k} data-testid={`admin-req-count-${k}`} style={{ padding: '3px 8px', borderRadius: 999,
              background: `${(STATUS_COLOR[k]) || theme.colors.text.muted}1A`,
              color: (STATUS_COLOR[k]) || theme.colors.text.muted, fontSize: 11, fontWeight: 700 }}>
              {k}: {v}
            </span>
          ))}
        </div>

        {loading ? (
          <div data-testid="admin-req-loading" style={{ padding: 18 }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div data-testid="admin-req-empty" style={{ padding: 24, textAlign: 'center', color: theme.colors.text.muted }}>
            No Architect requests match the current filter.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {rows.map(r => (
              <div key={r.id} data-testid={`admin-req-row-${r.id}`}
                style={{ padding: 12, background: theme.colors.bg.secondary, borderRadius: theme.radius.sm,
                  display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 280 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 800 }}>{r.kickback_name}</div>
                    <Pill status={r.status} />
                    <span style={{ fontSize: 11, color: theme.colors.text.muted }}>
                      {r.kickback_type} · {r.requested_specialty} · risk: {r.risk_category}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: theme.colors.text.muted, marginTop: 4 }}>
                    Business: <b>{r.business_name}</b> · Requestor: {r.creator_email}
                    {r.assigned_architect_id && <> · Architect: <code>{r.assigned_architect_id.slice(0, 8)}…</code></>}
                  </div>
                </div>
                <button data-testid={`admin-req-open-${r.id}`} onClick={() => { setSelected(r); setAssignId(r.assigned_architect_id || ''); setNotes(r.admin_notes || ''); }}
                  style={{ padding: '8px 12px', borderRadius: theme.radius.sm, border: 'none',
                    background: theme.colors.primary, color: '#fff', cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>
                  <Eye size={14} /> View
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 1000 }}>
          <div data-testid="admin-req-modal" style={{ ...cardStyle, width: '100%', maxWidth: 760, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 18, fontWeight: 900 }}>Architect Request Packet</div>
              <button data-testid="admin-req-close" onClick={() => setSelected(null)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontWeight: 800 }}>{selected.kickback_name}</div>
              <Pill status={selected.status} />
            </div>
            <div style={{ fontSize: 12, color: theme.colors.text.muted, marginBottom: 8 }}>
              Business: {selected.business_name} · Requestor: {selected.creator_email}
              · Specialty: {selected.requested_specialty} · Risk: {selected.risk_category}
            </div>
            <div style={{ display: 'grid', gap: 4, marginBottom: 10, fontSize: 12, color: theme.colors.text.secondary }}>
              {selected.program_summary && <div><b>Program:</b> {selected.program_summary}</div>}
              {selected.payout_style_rules && <div><b>Payout rules:</b> {selected.payout_style_rules}</div>}
              {selected.utility_setup_summary && <div><b>Utility setup:</b> {selected.utility_setup_summary}</div>}
              {selected.compliance_notes && <div><b>Compliance notes:</b> {selected.compliance_notes}</div>}
              {selected.architect_notes && <div><b>Architect notes:</b> {selected.architect_notes}</div>}
              {selected.decline_reason && <div><b>Decline reason:</b> {selected.decline_reason}</div>}
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Assign Approved Architect</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <select data-testid="admin-assign-select" style={{ ...inputStyle, flex: 1 }}
                  value={assignId} onChange={e => setAssignId(e.target.value)}>
                  <option value="">— choose approved architect —</option>
                  {architects.map(a => (
                    <option key={a.id} value={a.id}>{a.full_name} ({a.email})</option>
                  ))}
                </select>
                <button data-testid="admin-assign-btn" disabled={acting || !assignId} onClick={assign}
                  style={{ padding: '8px 12px', borderRadius: theme.radius.sm, border: 'none',
                    background: theme.colors.status.success, color: '#fff', cursor: 'pointer', fontWeight: 700,
                    display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <UserPlus size={14} /> Assign
                </button>
              </div>
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Admin Notes</label>
              <textarea data-testid="admin-req-notes-input" style={{ ...inputStyle, width: '100%', minHeight: 60 }}
                value={notes} onChange={e => setNotes(e.target.value)} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Rejection Reason</label>
              <textarea data-testid="admin-req-reject-input" style={{ ...inputStyle, width: '100%', minHeight: 60 }}
                value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} />
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button data-testid="admin-req-action-mark-admin-review" disabled={acting} onClick={() => reviewAction('mark_admin_review')}
                style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: 'none',
                  background: theme.colors.status.warning, color: '#fff', cursor: 'pointer', fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Clock size={14} /> Mark Admin Review
              </button>
              <button data-testid="admin-req-action-reject" disabled={acting} onClick={() => reviewAction('reject')}
                style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: 'none',
                  background: theme.colors.status.error, color: '#fff', cursor: 'pointer', fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <XCircle size={14} /> Reject
              </button>
              <button data-testid="admin-req-action-cancel" disabled={acting} onClick={() => reviewAction('cancel')}
                style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`,
                  background: theme.colors.bg.card, cursor: 'pointer', fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Ban size={14} /> Cancel
              </button>
              <button data-testid="admin-req-action-archive" disabled={acting} onClick={() => reviewAction('archive')}
                style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`,
                  background: theme.colors.bg.card, cursor: 'pointer', fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Archive size={14} /> Archive
              </button>
            </div>

            {/* Phase 6 — Assistant Draft (admin can generate/view for any request) */}
            <AssistantDraftPanel
              requestId={selected.id}
              role="admin"
              status={selected.status}
              architectApproved={true}
            />
          </div>
        </div>
      )}
    </div>
  );
}
